'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { analytics } from '@/lib/analytics/posthog'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { useIntercom } from '@/components/integrations/intercom/intercom-provider'
import { loadExternalScript } from '@/components/integrations/shared/load-script'
import { calendlyBooking } from '@/config/analytics'
import { attributionForManager, utmParameters } from '@/lib/analytics/attribution'
import { identifyByEmail, splitFullName } from '@/lib/analytics/identity'
import { firePixelConversion } from '@/lib/analytics/pixels'

type CalendlyContextValue = {
  open: (options?: {
    url?: string
    user?: string | null
    prefill?: { name?: string; email?: string }
    /** Stable slug of the entry point that opened the planner, carried onto `demo_opened`. */
    location?: string
  }) => void
  /** Preload the widget on hover/focus intent so `open` is instant without mount-time cost. */
  warmup: () => void
}

const CalendlyContext = createContext<CalendlyContextValue | null>(null)

const calendlyCssId = 'calendly-widget-css'
const calendlyJsId = 'calendly-widget-js'

/**
 * The booking page as we want it embedded.
 *
 * `hide_gdpr_banner` is the one that matters: without it Calendly puts its own cookie banner over
 * the calendar for anyone without their cookie — every first-time visitor — asking a second consent
 * question inside a dialog the visitor opened to pick a date. The colours make the frame agree with
 * the page it opens on.
 *
 * The stored UTMs ride along because Calendly keeps them on the invitee (`tracking.utm_*`) and sends
 * them in the `invitee.created` webhook — which is where the Manager creates `demo_request_received`,
 * and which usually beats the browser's own call to it. Only data inside the webhook is reliably
 * there when that conversion is created. A UTM the passed URL already carries is its own and stays.
 */
function embedUrl(url: string) {
  const embed = new URL(url)
  for (const [param, value] of Object.entries(utmParameters())) {
    if (value && !embed.searchParams.has(param)) {
      embed.searchParams.set(param, value)
    }
  }
  embed.searchParams.set('hide_gdpr_banner', '1')
  // `--color-accent-strong` rather than the accent itself: Calendly picks the label colour on a
  // primary-coloured button from its luminance, and the lighter accent lands just on the side where
  // it writes black on blue. The strong variant is the same blue, one step down, and gets white.
  embed.searchParams.set('primary_color', '1f6fe0')
  return embed.toString()
}

type CalendlyMessage = {
  event?: string
  payload?: {
    event?: { uri?: string }
    invitee?: { uri?: string }
  }
}

export function CalendlyProvider({ url, children }: { url: string; children: ReactNode }) {
  const requestMetaRef = useRef<{ user?: string | null; name?: string; email?: string } | null>(null)
  const { setLauncherVisible } = useIntercom()

  const ensureCalendlyLoaded = useCallback(() => {
    if (typeof window === 'undefined') {
      return Promise.resolve()
    }

    if (window.Calendly?.initPopupWidget) {
      return Promise.resolve()
    }

    if (!document.getElementById(calendlyCssId)) {
      const link = document.createElement('link')
      link.id = calendlyCssId
      link.rel = 'stylesheet'
      link.href = 'https://assets.calendly.com/assets/external/widget.css'
      document.head.appendChild(link)
    }

    return loadExternalScript({
      id: calendlyJsId,
      src: 'https://assets.calendly.com/assets/external/widget.js',
    }).then(() => {
      if (!window.Calendly?.initPopupWidget) {
        throw new Error('Calendly widget did not initialize')
      }
    })
  }, [])

  const open = useCallback(
    (options?: {
      url?: string
      user?: string | null
      prefill?: { name?: string; email?: string }
      location?: string
    }) => {
      requestMetaRef.current = {
        user: options?.user,
        name: options?.prefill?.name,
        email: options?.prefill?.email,
      }

      // Top of the demo funnel: the widget was opened (may or may not lead to a booking).
      // `location` is the entry point that opened it (nav, a page CTA, footer, …); undefined for
      // callers that don't pass one, which keeps every existing `demo_opened` caller unaffected.
      analytics.capture('demo_opened', { prefilled: Boolean(options?.prefill?.email), location: options?.location })

      // Staging books nothing. The planner is a real calendar belonging to a real person, so a
      // test environment that opens it fills that calendar with appointments someone has to cancel.
      // What staging does need to exercise is everything *after* the booking, so this runs the same
      // reporting path the real `calendly.event_scheduled` message runs, against a marked synthetic
      // invitee. It used to run a shortened version of its own that skipped identify and skipped the
      // pixel conversion entirely — so the one thing simulated mode existed to prove was the one
      // thing it never exercised.
      if (calendlyBooking === 'simulated') {
        void reportConversion(
          SIMULATED_INVITEE_URI,
          { email: options?.prefill?.email, name: options?.prefill?.name },
          true,
        )
        return
      }

      const openPopup = () => {
        if (!window.Calendly?.initPopupWidget) {
          return
        }
        window.Calendly.initPopupWidget({
          url: embedUrl(options?.url ?? url),
          prefill: options?.prefill,
        })

        // Intercom's launcher is drawn above everything, so on a phone it sits on the calendar.
        // It steps aside for as long as the planner is open, and comes back when it closes.
        setLauncherVisible(false)
        const overlay = document.querySelector('.calendly-overlay')
        if (overlay) {
          const observer = new MutationObserver(() => {
            if (!document.body.contains(overlay)) {
              observer.disconnect()
              setLauncherVisible(true)
            }
          })
          observer.observe(document.body, { childList: true })
        } else {
          setLauncherVisible(true)
        }
      }

      if (window.Calendly?.initPopupWidget) {
        openPopup()
        return
      }

      void ensureCalendlyLoaded()
        .then(openPopup)
        .catch(() => undefined)
    },
    [ensureCalendlyLoaded, setLauncherVisible, url],
  )

  const warmup = useCallback(() => {
    void ensureCalendlyLoaded().catch(() => undefined)
  }, [ensureCalendlyLoaded])

  const value = useMemo(() => ({ open, warmup }), [open, warmup])

  useEffect(() => {
    const handleMessage = async (event: MessageEvent<CalendlyMessage>) => {
      if (!event?.data || typeof event.data !== 'object') {
        return
      }
      if (!event.origin.includes('calendly.com')) {
        return
      }
      if (event.data.event !== 'calendly.event_scheduled') {
        return
      }

      await reportConversion(event.data.payload?.invitee?.uri, requestMetaRef.current ?? undefined)
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  return <CalendlyContext.Provider value={value}>{children}</CalendlyContext.Provider>
}

/**
 * The invitee handed to the Manager in simulated mode. Deliberately not a plausible Calendly URI:
 * whatever the Manager does with it, the record it leaves is obviously a test.
 */
const SIMULATED_INVITEE_URI = 'https://api.calendly.com/scheduled_events/SIMULATED/invitees/STAGING'

/**
 * Reports one booked demo: identifies the person (before the Manager call when the planner was
 * prefilled, after it otherwise), tells the Manager, records the funnel event and fires the browser
 * ad pixels.
 *
 * Both entry points run this — the real `calendly.event_scheduled` message and the simulated
 * booking staging uses — so there is one description of what a booked demo does rather than two
 * that can drift. `simulated` only tags the PostHog event; everything else is identical, which is
 * the point.
 */
async function reportConversion(
  /** Absent when Calendly reports a booking without one — the conversion is still recorded, from
   * whatever the planner was prefilled with, because no Manager call means no server-side half to
   * deduplicate against and a silent drop would lose the booking entirely. */
  inviteeUri: string | undefined,
  metadata?: { user?: string | null; name?: string; email?: string },
  simulated = false,
): Promise<void> {
  // A prefilled planner — a campaign link that names the person — knows the email before the
  // Manager is told, so the browser identifies first and stays the first writer of the person's
  // first touch (ADR-0010; the Manager captures `demo_request_received` inside the request below).
  // Without a prefill the email only arrives with the response, and there is no earlier truth to
  // protect.
  if (metadata?.email) {
    identifyInvitee(metadata.email, metadata.name)
  }

  const responseData = inviteeUri ? await reportDemoBooked(inviteeUri, metadata?.user ?? undefined) : null

  const name = metadata?.name ?? responseData?.name
  const email = metadata?.email ?? responseData?.email
  // The backend returns the shared dedup eventId (`lead.<sha256(email)>`).
  const eventId = responseData?.eventId

  // A booked demo is a key conversion. Email/name are PII, so identify the person when we have
  // their email and keep the event free of PII. Without a prefill this is the first identify; with
  // one, the Manager's answer may still add a name the prefill did not carry, and identifying early
  // must not mean identifying with less. A second `identify()` for the same distinct id sends no
  // second `$identify` — posthog-js turns it into a person-property write.
  const identifiedBeforeTheCall = Boolean(metadata?.email)
  if (email && (!identifiedBeforeTheCall || (!metadata?.name && name))) {
    identifyInvitee(email, name)
  }

  analytics.capture('demo_scheduled', { conversion_id: eventId, ...(simulated ? { simulated: true } : {}) })

  // Fire the browser ad pixels, deduped against the server-side CAPI by eventId.
  firePixelConversion('demo', {
    eventId,
    // The Manager returns the person's uuid on this endpoint too; without it the demo pixel was
    // the only conversion still sending no identity.
    externalId: responseData?.externalId,
    email,
    ...splitFullName(name),
    // A second booking within 90 days is the same demo (workspace ADR 0015).
    repeat: responseData?.repeat === true,
  })
}

/**
 * `first_name`/`last_name`, the two the CDP destinations read — not a single `name`, which nothing
 * consumes and which the Manager never writes. Split on the first space so a tussenvoegsel stays
 * with the surname: "Jan van der Berg" is Jan, van der Berg.
 */
function identifyInvitee(email: string, name: string | undefined): void {
  const { firstName, lastName } = splitFullName(name)
  identifyByEmail(email, { first_name: firstName, last_name: lastName })
}

/**
 * Tells the Manager a demo was booked, and hands back the shared dedup event id.
 */
async function reportDemoBooked(
  inviteeUri: string,
  user: string | undefined,
): Promise<{ eventId?: string; externalId?: string; name?: string; email?: string; repeat?: boolean } | null> {
  try {
    const response = await fetch(`/api/request-demo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // The campaign as well, so the lead learns it even if Calendly ever dropped its tracking
      // fields, and so the simulated staging booking has a request to read it from.
      body: JSON.stringify({ ...attributionForManager(), url: inviteeUri, user }),
    })
    return response.ok ? await response.json().catch(() => null) : null
  } catch {
    return null
  }
}

export function useCalendly() {
  const context = useContext(CalendlyContext)
  if (!context) {
    throw new Error('useCalendly must be used within a CalendlyProvider')
  }
  return context
}
